import {useMemo, useState} from "react";
import {APIProvider, ControlPosition, Map} from "@vis.gl/react-google-maps";
import {useQuery} from "@tanstack/react-query";
import {FaSearch} from "react-icons/fa";
import corporationService from "../../../../../../services/web/corporation-service.js";
import vmsService, {VMS_QUERY_KEYS} from "../../../../../../services/web/vms-service.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import Button from "../../../../../../components/button/button.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import CftvMapFitBounds from "./cftv-map-fit-bounds.jsx";
import CftvMapCameraFocus from "./cftv-map-camera-focus.jsx";
import CftvMapMarkers from "./cftv-map-markers.jsx";
import CftvCameraDetails from "./cftv-camera-details.jsx";
import IntegrationsTable from "./integrations-table.jsx";

const RADIUS_METERS = 1000;
const MAP_BOUNDS_RADIUS_METERS = 600;
const MAP_TAB = "map";
const INTEGRATIONS_TAB = "integrations";

const tabStyle = "h-8 rounded-t border border-b-0 border-gray-300 px-4 font-semibold text-[#676a6c]";
const tabStyleActive = "bg-white";
const tabStyleInactive = "bg-[#eef0f3] hover:bg-gray-200";

const CftvMap = ({center, laneCode, integrations, onAssociate, onRemove, onOffsetChange}) => {
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [cameraToFind, setCameraToFind] = useState(null);
    const [focusedCamera, setFocusedCamera] = useState(null);
    const [zoom, setZoom] = useState(15);
    const [activeTab, setActiveTab] = useState(MAP_TAB);
    const isMapTab = activeTab === MAP_TAB;

    const corporation = useQuery({
        queryKey: ["corporation"],
        queryFn: () => corporationService.entity(),
    });
    const nearby = useQuery({
        queryKey: VMS_QUERY_KEYS.nearby(center.lat, center.lng, RADIUS_METERS, laneCode),
        queryFn: () => vmsService.getNearby({latitude: center.lat, longitude: center.lng, radiusMeters: RADIUS_METERS, laneCode}),
    });
    const mappable = useQuery({
        queryKey: VMS_QUERY_KEYS.mappable(center.lat, center.lng, laneCode),
        queryFn: () => vmsService.getMappable({latitude: center.lat, longitude: center.lng, laneCode}),
    });

    const mapCameras = useMemo(() => {
        const nearbyCameras = nearby.data ?? [];

        if (!focusedCamera || nearbyCameras.some(camera =>
            Number(camera.id) === Number(focusedCamera.id) && Number(camera.vmsId) === Number(focusedCamera.vmsId))) {
            return nearbyCameras;
        }

        return [...nearbyCameras, focusedCamera];
    }, [focusedCamera, nearby.data]);
    const mappableCameraOptions = useMemo(() => (mappable.data ?? []).map(camera => ({
        value: `${camera.vmsId}-${camera.id}`,
        label: `${camera.id} - ${camera.name} (${camera.vmsName})`,
        camera,
    })), [mappable.data]);

    const integration = useMemo(() => selectedCamera ? integrations.find(item =>
        Number(item.id) === Number(selectedCamera.id) && Number(item.vmsId) === Number(selectedCamera.vmsId)) : null,
    [integrations, selectedCamera]);

    if (corporation.isLoading || nearby.isLoading || mappable.isLoading) {
        return <div className="flex h-[480px] animate-pulse items-center justify-center rounded bg-gray-100 text-sm text-gray-500">Carregando mapa...</div>;
    }

    if (corporation.isError || nearby.isError || mappable.isError) {
        return (
            <div className="flex h-[480px] flex-col items-center justify-center gap-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
                Não foi possível carregar as câmeras próximas.
                <Button type="button" onClick={() => { corporation.refetch(); nearby.refetch(); mappable.refetch(); }}>Tentar novamente</Button>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 overflow-hidden rounded border border-gray-300 bg-white lg:h-[520px] lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
            <div className="flex h-[520px] min-h-0 flex-col lg:h-full">
                <div className="flex h-10 shrink-0 items-end gap-1 border-b border-gray-300 px-2">
                    <button
                        type="button"
                        className={tabStyle + " " + (isMapTab ? tabStyleActive : tabStyleInactive)}
                        onClick={() => setActiveTab(MAP_TAB)}
                    >
                        Mapa
                    </button>
                    <button
                        type="button"
                        className={tabStyle + " " + (isMapTab ? tabStyleInactive : tabStyleActive)}
                        onClick={() => setActiveTab(INTEGRATIONS_TAB)}
                    >
                        Tabela
                    </button>
                </div>

                <div className="relative min-h-0 flex-1">
                    <div className={"absolute inset-0" + (isMapTab ? "" : " invisible")}>
                        <APIProvider apiKey={corporation.data?.maps_api_key ?? ""} language="pt-BR">
                            <Map
                                mapId={projectUtils.googleMapsIds().default}
                                defaultCenter={center}
                                defaultZoom={15}
                                streetViewControl={false}
                                mapTypeControl={true}
                                mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                                fullscreenControl={true}
                                fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                                scrollwheel={true}
                                gestureHandling="greedy"
                                style={{height: "100%", width: "100%"}}
                                onCameraChanged={({detail}) => setZoom(detail.zoom)}
                            >
                                <CftvMapFitBounds center={center} cameras={nearby.data ?? []} radiusMeters={MAP_BOUNDS_RADIUS_METERS}/>
                                <CftvMapCameraFocus camera={focusedCamera}/>
                                <CftvMapMarkers
                                    center={center}
                                    cameras={mapCameras}
                                    integrations={integrations}
                                    selectedCamera={selectedCamera}
                                    onSelect={setSelectedCamera}
                                    zoom={zoom}
                                />
                            </Map>
                        </APIProvider>

                        { isMapTab &&
                            <div className="absolute left-3 top-3 z-10 flex max-w-[calc(100%-1.5rem)] items-center gap-2">
                                <div className="w-[160px] rounded bg-white p-1 shadow">
                                    <SelectComponent
                                      label="Câmeras VMS"
                                      value={cameraToFind}
                                      options={mappableCameraOptions}
                                      placeholder="Procurar câmera pelo ID"
                                      onChange={setCameraToFind}
                                      hideOptionPath={true}
                                    />
                                </div>
                                <Button
                                  title="Procurar câmera"
                                  disabled={!cameraToFind}
                                  onClick={() => {
                                      setFocusedCamera({...cameraToFind.camera});
                                      setSelectedCamera(cameraToFind.camera);
                                      setActiveTab(MAP_TAB);
                                  }}
                                >
                                    <FaSearch/>
                                </Button>
                            </div>
                        }

                        {!nearby.data?.length && (
                            <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded bg-white px-4 py-2 text-center text-sm text-gray-600 shadow">
                                Nenhuma câmera VMS encontrada em um raio de 1 km.
                            </div>
                        )}
                    </div>

                    {!isMapTab && (
                        <div className="absolute inset-0 overflow-auto bg-white p-3">
                            {integrations.length
                                ? <IntegrationsTable integrationData={integrations} onSelect={setSelectedCamera}/>
                                : <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">Nenhuma câmera associada a esta faixa.</div>}
                        </div>
                    )}
                </div>
            </div>

            <div className="min-h-[220px] min-w-0 overflow-hidden border-t border-gray-300 lg:min-h-0 lg:border-l lg:border-t-0">
                <CftvCameraDetails
                    camera={selectedCamera}
                    integration={integration}
                    onAssociate={onAssociate}
                    onRemove={onRemove}
                    onOffsetChange={onOffsetChange}
                />
            </div>
        </div>
    );
};

export default CftvMap;
